import t from 'react-translate';
import { pick } from 'underscore';

import { useSelector } from 'react-redux';
import { getCourseAliases } from 'redux/selectors/course';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { innerPayloadKey } from 'redux/schemas/models/activity';

const useAiSuggestComponents = () => {
  const aliases = useSelector((state) => getCourseAliases(state));

  const componentTextMapping = {
    [ComponentType.PROFILE_COMPLETION]: t.LECTURE_PAGES.COMPONENTS.PROFILE_COMPLETION.DESCRIPTION(),
    [ComponentType.MEET_AND_GREET]: t.LECTURE_PAGES.COMPONENTS.MEET_AND_GREET.DESCRIPTION(),
    [ComponentType.TOPIC]: t.LECTURE_PAGES.COMPONENTS.DISCUSSION.DESCRIPTION(),
    [ComponentType.QUIZ]: t.LECTURE_PAGES.COMPONENTS.QUIZ.DESCRIPTION(),
    [ComponentType.POLL]: t.LECTURE_PAGES.COMPONENTS.POLLS.DESCRIPTION(),
    [ComponentType.EXERCISE]: t.LECTURE_PAGES.COMPONENTS.EXERCISE.DESCRIPTION(aliases.assignmentAliases),
    [ComponentType.PUBLIC_PEER_EVALUATION]: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.ASSIGNMENT.PUBLIC(),
    [ComponentType.SURVEY]: t.LECTURE_PAGES.COMPONENTS.SURVEY.DESCRIPTION(),
  };

  const componentIconMapping = {
    [ComponentType.PROFILE_COMPLETION]: 'profile',
    [ComponentType.MEET_AND_GREET]: 'meet-your-peers',
    [ComponentType.TOPIC]: 'conversations',
    [ComponentType.QUIZ]: 'quiz',
    [ComponentType.POLL]: 'polls',
    [ComponentType.EXERCISE]: 'assignments',
    [ComponentType.PUBLIC_PEER_EVALUATION]: 'comments',
    [ComponentType.SURVEY]: 'survey',
  };

  // BE assumes question set components have similar payload structure
  const getQuestionComponentPayload = (lc) => {
    const typekey = innerPayloadKey[lc.type];

    return {
      ...pick(lc, ['trueType', 'type']),
      [typekey]: {
        ...pick(lc[typekey], [
          'title', 'expirationDate', 'graded', 'hardDeadline',
          'isTodo', 'maximumAttempts',
        ]),
        questionsAttributes: lc[typekey].questions.map((question) => ({
          ...pick(question, ['questionText', 'type']),
          responseOptionsAttributes: question.responseOptions.map((option) => (
            pick(option, ['optionContent', 'isCorrect', 'explanation']))),
        })),
      },
    };
  };

  /**
   * The payload is being constructed to match the existing component
   * creation payload structure, using the data from the AI-suggested components.
   * @param component AI-suggested component
   * @returns constructed payload object
   */
  const getComponentPayload = (component) => {
    switch (component.trueType) {
      case ComponentType.MEET_AND_GREET:
        return {
          ...pick(component, 'trueType', 'type'),
          meetAndGreet: pick(
            component.meetAndGreet,
            'answerableId', 'answerableType', 'defaultProfileQuestion',
            'photosRequired', 'sortBy',
          ),
        };
      case ComponentType.POLL:
      case ComponentType.QUIZ:
      case ComponentType.SURVEY:
        return getQuestionComponentPayload(component);
      case ComponentType.TOPIC:
        return {
          ...pick(component, 'trueType', 'type'),
          topic: {
            aiAction: NovoAIItemType.SUGGEST_ACTIVITIES,
            ...(component.topic.forumId
              ? { forumId: component.topic.forumId }
              : { forumTitle: component.topic.forum.title }),
            ...pick(component.topic, 'title', 'body', 'starterId', 'expandedOnLoad'),
          },
        };
      case ComponentType.EXERCISE:
        return {
          ...pick(component, 'trueType', 'type'),
          exercise: {
            ...pick(
              component.exercise,
              'title', 'name', 'description', 'formedByStudents', 'privacySetting',
              'isTodo', 'hardDeadline', 'deadline', 'teamType', 'template',
            ),
          },
        };
      case ComponentType.PUBLIC_PEER_EVALUATION:
        return {
          ...pick(component, 'trueType', 'type'),
          peerEvaluation: {
            ...pick(
              component.peerEvaluation,
              'exerciseId', 'hardDeadline', 'isTodo', 'targetGoal', 'title',
            ),
            description: `<div class="froala-style-regular">${component.peerEvaluation.description}</div>`,
          },
        };
      default:
        return component;
    }
  };

  return {
    componentTextMapping,
    componentIconMapping,
    getComponentPayload,
  };
};

export default useAiSuggestComponents;
